// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useEffect, useMemo, useState } from 'react';

import { Text } from '@/components/Themed';
import appColors, { alpha } from '@/theme/colors';

type Props = {
  visible: boolean;
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  nameEditable?: boolean;
  /**
   * Warning to show under the name field, resolved from the name as it is typed. Used to tell the
   * caller they already have a player with this name so they can pick that one instead of creating
   * a second row for the same person.
   */
  nameNotice?: (name: string) => string | undefined;
  hcpLabel: string;
  hcpHelp: string;
  hcpHint?: string;
  saveLabel: string;
  closeLabel: string;
  initialName: string;
  initialScp: string;
  minHcp?: number;
  maxHcp?: number;
  onSave: (name: string, scp: string) => void;
  onClose: () => void;
};

const DEFAULT_MIN_HCP = -10;
const DEFAULT_MAX_HCP = 54;

const parseHcp = (value: string): number => Number(value.trim().replace(',', '.'));

export default function PlayerEditModal({
  visible,
  title,
  nameLabel,
  namePlaceholder,
  nameEditable = true,
  nameNotice,
  hcpLabel,
  hcpHelp,
  hcpHint,
  saveLabel,
  closeLabel,
  initialName,
  initialScp,
  minHcp = DEFAULT_MIN_HCP,
  maxHcp = DEFAULT_MAX_HCP,
  onSave,
  onClose,
}: Props) {
  const [name, setName] = useState(initialName);
  const [scp, setScp] = useState(initialScp);

  // Reseed the fields every time the modal opens for a (possibly different) player.
  useEffect(() => {
    if (visible) {
      setName(initialName);
      setScp(initialScp);
    }
  }, [visible, initialName, initialScp]);

  const { parsed, isValid } = useMemo(() => {
    const trimmed = scp.trim();
    const value = parseHcp(trimmed);
    const valid = trimmed.length > 0 && Number.isFinite(value) && value >= minHcp && value <= maxHcp;
    return { parsed: value, isValid: valid };
  }, [scp, minHcp, maxHcp]);

  const notice = useMemo(() => (nameEditable ? nameNotice?.(name) : undefined), [name, nameEditable, nameNotice]);

  const step = (delta: number) => {
    const base = Number.isFinite(parsed) ? parsed : 0;
    const next = Math.min(maxHcp, Math.max(minHcp, Math.round(base) + delta));
    setScp(String(next));
  };

  const handleSave = () => {
    if (!isValid) return;
    onSave(nameEditable ? name.trim() : initialName, scp.trim());
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null}>
          <View style={styles.headerRow}>
            <Text style={styles.title} numberOfLines={1}>
              {title}
            </Text>
            <Pressable style={styles.closeButton} onPress={onClose} accessibilityLabel={closeLabel}>
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          {nameEditable ? (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>{nameLabel}</Text>
              <TextInput
                value={name}
                onChangeText={setName}
                placeholder={namePlaceholder}
                placeholderTextColor={appColors.text.muted}
                style={styles.nameInput}
              />
              {notice ? <Text style={styles.fieldWarning}>{notice}</Text> : null}
            </View>
          ) : null}

          <View style={styles.field}>
            <Text style={styles.fieldLabel}>{hcpLabel}</Text>
            <Text style={styles.fieldHelp}>{hcpHelp}</Text>
            <View style={styles.stepperRow}>
              <Pressable style={styles.stepButton} onPress={() => step(-1)} accessibilityLabel="-1">
                <Text style={styles.stepButtonText}>–</Text>
              </Pressable>
              <TextInput
                value={scp}
                onChangeText={setScp}
                keyboardType="numbers-and-punctuation"
                placeholder="0"
                placeholderTextColor={appColors.accent.teal}
                style={styles.hcpInput}
                selectTextOnFocus
                accessibilityLabel={hcpLabel}
              />
              <Pressable style={styles.stepButton} onPress={() => step(1)} accessibilityLabel="+1">
                <Text style={styles.stepButtonText}>+</Text>
              </Pressable>
            </View>
            {hcpHint ? <Text style={styles.fieldHint}>{hcpHint}</Text> : null}
          </View>

          <Pressable
            style={[styles.saveButton, !isValid && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={!isValid}
            accessibilityLabel={saveLabel}
          >
            <Text style={[styles.saveButtonText, !isValid && styles.saveButtonTextDisabled]}>{saveLabel}</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.5),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 16,
    gap: 14,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: appColors.text.muted,
    fontSize: 16,
    fontWeight: '700',
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  fieldHelp: {
    color: appColors.text.muted,
    fontSize: 12,
    lineHeight: 17,
  },
  fieldHint: {
    color: appColors.accent.primary,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 8,
  },
  fieldWarning: {
    color: appColors.accent.gold,
    fontSize: 12,
    fontWeight: '700',
    marginTop: 8,
  },
  nameInput: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: appColors.surface.input,
    color: appColors.text.primary,
    fontSize: 16,
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 4,
  },
  stepButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepButtonText: {
    color: appColors.accent.primary,
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 26,
  },
  hcpInput: {
    minWidth: 84,
    height: 52,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 0,
    backgroundColor: appColors.surface.input,
    color: appColors.text.primary,
    fontSize: 32,
    fontWeight: '700',
    textAlign: 'center',
  },
  saveButton: {
    backgroundColor: appColors.background.interactive,
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: appColors.border.strong,
  },
  saveButtonText: {
    color: appColors.text.inverse,
    fontSize: 14,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  saveButtonTextDisabled: {
    color: appColors.text.muted,
  },
});
